import { getGuestToken, clearGuestToken } from './guestToken';
import { uploadFile, type UploadProgress } from './uploadService';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Error thrown for non-2xx responses. Extends Error (existing `instanceof Error`
 * handling keeps working) and additionally carries the HTTP status, the backend
 * error code (the `error` field of the standard GlobalExceptionHandler body,
 * e.g. VAT_STATUS_NOT_DECLARED / TRN_REQUIRED_FOR_VAT_REGISTERED / INVOICE_NOT_READY)
 * and any field-level `errors` map.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly fieldErrors: Record<string, string> | null;
  /**
   * The `code` field of the body, when the backend sends one next to `error`
   * (Spec 062 offers: GARAGE_BLOCKED, OFFER_STATE_CHANGED, …). `code` above keeps
   * reading `error` so existing callers are unchanged.
   */
  readonly errorCode: string | null;
  /** The body's `details` object, when sent (#995 PRICE_BELOW_OFFER_DISCOUNT: offer + minimum price). */
  readonly details: Record<string, unknown> | null;
  /** The whole error body as sent (spec 071: 409 TERMS_VERSION_STALE carries `staleDocumentIds`). */
  readonly body: Record<string, unknown> | null;

  constructor(
    message: string,
    status: number,
    code: string | null,
    fieldErrors: Record<string, string> | null,
    errorCode: string | null = null,
    details: Record<string, unknown> | null = null,
    body: Record<string, unknown> | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
    this.errorCode = errorCode;
    this.details = details;
    this.body = body;
  }
}

interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  data?: unknown;
  token?: string;
  signal?: AbortSignal;
  // Defaults: GET/HEAD/OPTIONS/PUT/DELETE/PATCH retry once on network error;
  // POST does NOT (callers opt in for idempotent POSTs like /api/garages/init).
  retry?: boolean;
  /** Extra request headers (Spec 085: the dealer booking quote declares X-Garagat-Capabilities). */
  headers?: Record<string, string>;
}

export async function request<T>({ method, url, data, token, signal, retry, headers: extraHeaders }: RequestOptions): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...extraHeaders,
  };

  // Logged-in requests (Bearer) are left byte-for-byte unchanged. Tokenless
  // requests attach a guest token so the gateway doesn't reject them with
  // 401 { "error": "guest_token_required" }.
  const isGuest = !token;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    try {
      headers['X-Guest-Token'] = await getGuestToken();
    } catch {
      // Couldn't mint a guest token — proceed without it and let the real
      // request 401 naturally rather than crashing the app.
    }
  }

  const config: RequestInit = { method, headers, signal };

  if (data && method !== 'GET') {
    config.body = JSON.stringify(data);
  }

  const shouldRetry = retry ?? method !== 'POST';

  // Single retry on TypeError (network/connection-reset, e.g. during an
  // ECS rolling deploy the previous HTTP/2 stream's backend died mid-flight).
  // 400 ms gives the browser time to open a fresh HTTP/2 connection.
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${url}`, config);
  } catch (err) {
    if (shouldRetry && err instanceof TypeError && !signal?.aborted) {
      await new Promise(r => setTimeout(r, 400));
      response = await fetch(`${API_BASE_URL}${url}`, config);
    } else {
      throw err;
    }
  }

  // Guest-token 401 recovery: a stale/expired guest token yields
  // 401 { "error": "guest_token_required" }. Mint a fresh one and retry once.
  // Only for guest requests — the Bearer path is untouched. The body can be
  // read only once, so read text first, then parse.
  if (isGuest && response.status === 401) {
    const peeked = await response.text().catch(() => '');
    let isGuestTokenError = false;
    try {
      isGuestTokenError = JSON.parse(peeked)?.error === 'guest_token_required';
    } catch {
      isGuestTokenError = false;
    }
    if (isGuestTokenError) {
      clearGuestToken();
      try {
        headers['X-Guest-Token'] = await getGuestToken();
        response = await fetch(`${API_BASE_URL}${url}`, config);
      } catch {
        // Re-mint failed — fall through with the original 401 reconstructed.
        response = new Response(peeked || '', { status: 401 });
      }
    } else {
      // Not a guest-token error — reconstruct the consumed body for the
      // generic error handler below.
      response = new Response(peeked || '', {
        status: 401,
        statusText: response.statusText,
      });
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    let message = errorData.message || `HTTP error! status: ${response.status}`;
    if (errorData.errors && typeof errorData.errors === 'object') {
      const fieldErrors = Object.entries(errorData.errors)
        .map(([field, msg]) => `${field}: ${msg}`)
        .join('; ');
      if (fieldErrors) {
        message += ` (${fieldErrors})`;
      }
    }
    throw new ApiError(
      message,
      response.status,
      typeof errorData.error === 'string' ? errorData.error : null,
      errorData.errors && typeof errorData.errors === 'object'
        ? (errorData.errors as Record<string, string>)
        : null,
      typeof errorData.code === 'string' ? errorData.code : null,
      errorData.details && typeof errorData.details === 'object'
        ? (errorData.details as Record<string, unknown>)
        : null,
      errorData && typeof errorData === 'object' ? (errorData as Record<string, unknown>) : null,
    );
  }

  const text = await response.text();
  return text ? JSON.parse(text) : ({} as T);
}

export const api = {
  get: <T>(url: string, token?: string) => request<T>({ method: 'GET', url, token }),
  post: <T>(url: string, data: unknown, token?: string) => request<T>({ method: 'POST', url, data, token }),
  put: <T>(url: string, data: unknown, token?: string) => request<T>({ method: 'PUT', url, data, token }),
  patch: <T>(url: string, data?: unknown, token?: string) => request<T>({ method: 'PATCH', url, data, token }),
  delete: <T>(url: string, token?: string) => request<T>({ method: 'DELETE', url, token }),

  /**
   * Request a password reset by username (partner portal) OR email (legacy fallback).
   * Backend dispatcher picks the right path based on which field is present.
   */
  requestPasswordReset: (identifier: { username?: string; email?: string }) =>
    request<void>({ method: 'POST', url: '/api/auth/password-reset', data: identifier }),

  loginByUsername: (username: string, password: string) =>
    request<import('../types').TokenResponse>({
      method: 'POST',
      url: '/api/auth/login/username',
      data: { username, password },
    }),

  checkUsernameAvailability: (username: string) =>
    request<import('../types').UsernameAvailabilityResponse>({
      method: 'GET',
      url: `/api/auth/username/availability?username=${encodeURIComponent(username)}`,
    }),

  recoverUsername: (data: import('../types').UsernameRecoveryRequest) =>
    request<void>({ method: 'POST', url: '/api/auth/username-recovery', data }),

  acceptTerms: (token: string) =>
    request<import('../types').OnboardingStatus>({ method: 'POST', url: '/api/users/me/accept-terms', data: {}, token }),

  acceptPartnershipPolicy: (token: string) =>
    request<import('../types').OnboardingStatus>({ method: 'POST', url: '/api/users/me/accept-partnership-policy', data: {}, token }),

  // Garage endpoints
  initGarage: (token: string, signal?: AbortSignal) =>
    request<import('../types').GarageResponse>({
      method: 'POST', url: '/api/garages/init', data: {}, token, signal,
      // Idempotent on the backend: "Creates or returns existing garage record."
      retry: true,
    }),

  createGarage: (data: unknown, token: string) =>
    request<import('../types').GarageResponse>({ method: 'POST', url: '/api/garages', data, token }),

  getMyGarage: (token: string) =>
    request<import('../types').GarageDetailResponse>({ method: 'GET', url: '/api/garages/me', token }),

  updateMyGarage: (data: unknown, token: string) =>
    request<import('../types').GarageResponse>({ method: 'PUT', url: '/api/garages/me', data, token }),

  // Spec 051 — Partner Settings VAT declaration (AC-19).
  // 422 TRN_REQUIRED_FOR_VAT_REGISTERED when vatRegistered=true without a 15-digit TRN.
  updateMyVat: (data: import('../types').UpdateVatRequest, token: string) =>
    request<import('../types').VatInfoResponse>({ method: 'PUT', url: '/api/garages/me/vat', data, token }),

  updateDraft: (data: import('../types').UpdateDraftRequest, token: string) =>
    request<import('../types').GarageDetailResponse>({ method: 'PATCH', url: '/api/garages/me/draft', data, token }),

  finalizeDraft: (data: import('../types').FinalizeDraftRequest, token: string) =>
    request<import('../types').GarageDetailResponse>({ method: 'POST', url: '/api/garages/me/finalize', data, token }),

  getActiveTermsDocuments: () =>
    request<import('../types').TermsDocumentPublic[]>({ method: 'GET', url: '/api/garages/terms-documents/active' }),

  // Spec 071 — signed terms agreement. All three are @RequireGarageOwner.
  getTermsSigningContext: (token: string) =>
    request<import('../types').TermsSigningContext>({ method: 'GET', url: '/api/garages/me/terms-signing', token }),

  getMySignedAgreements: (token: string) =>
    request<import('../types').SignedAgreement[]>({ method: 'GET', url: '/api/garages/me/signed-agreements', token }),

  /** 409 PDF_NOT_READY while the PDF is still being prepared; 404 for another garage's record. */
  getMySignedAgreementDownloadUrl: (recordId: string, token: string) =>
    request<import('../types').SignedAgreementDownloadUrl>({
      method: 'GET',
      url: `/api/garages/me/signed-agreements/${encodeURIComponent(recordId)}/download-url`,
      token,
    }),

  getMyTermsAgreements: (token: string) =>
    request<unknown[]>({ method: 'GET', url: '/api/garages/me/terms-agreements', token }),

  getMyHasServices: (token: string) =>
    request<{ hasServices: boolean; serviceCount: number }>({ method: 'GET', url: '/api/garages/me/has-services', token }),

  setOperatingHours: (data: import('../types').OperatingHoursRequest[], token: string) =>
    request<import('../types').OperatingHoursResponse[]>({ method: 'PUT', url: '/api/garages/me/hours', data, token }),

  // Media endpoints
  deleteMedia: (mediaId: string, token: string) =>
    request<void>({ method: 'DELETE', url: `/api/garages/me/media/${mediaId}`, token }),

  getDownloadUrl: (storageKey: string, token: string) =>
    request<import('../types').DownloadUrlResponse>({ method: 'GET', url: `/api/garages/me/download-url?storageKey=${encodeURIComponent(storageKey)}`, token }),

  // Service category endpoints
  getCategories: (activeOnly = true) =>
    request<import('../types').ServiceCategoryResponse[]>({ method: 'GET', url: `/api/garages/categories?activeOnly=${activeOnly}` }),

  // Flat list of ALL active categories (parents + children) each carrying parentId/parentName —
  // needed for parent + subcategory dropdowns (the default tree response nests children, so a
  // `parentId === selected` filter finds nothing).
  getCategoriesFlat: () =>
    request<import('../types').ServiceCategoryResponse[]>({ method: 'GET', url: `/api/garages/categories?flat=true` }),

  // Dashboard endpoint
  getMyDashboard: (token: string) =>
    request<import('../types').PartnerDashboardResponse>({ method: 'GET', url: '/api/garages/me/dashboard', token }),

  // Service rejection endpoint
  getServiceRejections: (token: string) =>
    request<import('../types').ServiceRejection[]>({ method: 'GET', url: '/api/garages/me/services/rejections', token }),

  // Service offering endpoints (owner)
  getMyServices: (token: string) =>
    request<import('../types').ServiceOfferingResponse[]>({ method: 'GET', url: '/api/garages/me/services', token }),

  addService: (data: import('../types').CreateServiceOfferingRequest, token: string) =>
    request<import('../types').ServiceOfferingResponse>({ method: 'POST', url: '/api/garages/me/services', data, token }),

  updateService: (offeringId: string, data: import('../types').UpdateServiceOfferingRequest, token: string) =>
    request<import('../types').ServiceOfferingResponse>({ method: 'PUT', url: `/api/garages/me/services/${offeringId}`, data, token }),

  removeService: (offeringId: string, token: string) =>
    request<void>({ method: 'DELETE', url: `/api/garages/me/services/${offeringId}`, token }),

  /** Owner view of my garage's media — the only source of mediaId. Includes documents. */
  getMyGarageMedia: (token: string) =>
    request<import('../types').MediaResponse[]>({ method: 'GET', url: '/api/garages/me/media', token }),

  /**
   * Set the order my garage's gallery photos appear in. Send every gallery-photo id in the
   * intended order; the logo is the cover image, not a gallery slide, and its id is rejected.
   * Applied immediately — reordering is presentation, so it does not go through the
   * change-request approval gate the way a content edit does.
   */
  reorderGarageMedia: (orderedIds: string[], token: string) =>
    request<import('../types').MediaResponse[]>({
      method: 'PUT', url: '/api/garages/me/media/reorder', data: { orderedIds }, token,
    }),

  /**
   * Set the order services appear in on the garage profile. Send every service id in the
   * intended order — the server assigns the positions, and rejects a partial list rather than
   * renumbering a stale subset. Returns the re-ordered list.
   */
  reorderServices: (orderedIds: string[], token: string) =>
    request<import('../types').ServiceOfferingResponse[]>({
      method: 'PUT', url: '/api/garages/me/services/reorder', data: { orderedIds }, token,
    }),

  /** Same contract, for one service's gallery photos. The thumbnail is not included. */
  reorderServiceMedia: (offeringId: string, orderedIds: string[], token: string) =>
    request<import('./uploadService').ServiceOfferingMedia[]>({
      method: 'PUT', url: `/api/garages/me/services/${offeringId}/media/reorder`,
      data: { orderedIds }, token,
    }),

  // ── Spec 044: V2 declarative offering endpoints ──
  // Backwards-compatible with v1 above. Mobile / partner portal adopt incrementally.

  createOfferingV2: (
    data: import('../types/offeringsV2').CreateOfferingV2Request,
    token: string,
  ) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'POST',
      url: '/api/garages/me/services-v2',
      data,
      token,
    }),

  listMyOfferingsV2: (token: string) =>
    request<import('../types/offeringsV2').OfferingV2Response[]>({
      method: 'GET',
      url: '/api/garages/me/services-v2',
      token,
    }),

  getMyOfferingV2: (offeringId: string, token: string) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'GET',
      url: `/api/garages/me/services-v2/${offeringId}`,
      token,
    }),

  getPublicOfferingV2: (garageId: string, offeringId: string, token?: string) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'GET',
      url: `/api/garages/${garageId}/services-v2/${offeringId}`,
      token,
    }),

  resolveQuoteV2: (
    garageId: string,
    offeringId: string,
    data: import('../types/offeringsV2').ResolveQuoteRequest,
    token?: string,
  ) =>
    request<import('../types/offeringsV2').ResolveQuoteResponse>({
      method: 'POST',
      url: `/api/garages/${garageId}/services-v2/${offeringId}/resolve-quote`,
      data,
      token,
    }),

  // ── Spec 044 v2: per-car variant management ──

  addCarToOfferingV2: (
    offeringId: string,
    payload: import('../types/offeringsV2').SupportedCarPayload,
    token: string,
  ) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'POST',
      url: `/api/garages/me/services-v2/${offeringId}/cars`,
      data: payload,
      token,
    }),

  removeCarFromOfferingV2: (offeringId: string, carId: string, token: string) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'DELETE',
      url: `/api/garages/me/services-v2/${offeringId}/cars/${carId}`,
      token,
    }),

  upsertCarVariantsV2: (
    offeringId: string,
    carId: string,
    data: import('../types/offeringsV2').UpsertCarVariantsRequest,
    token: string,
  ) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'POST',
      url: `/api/garages/me/services-v2/${offeringId}/cars/${carId}/variants`,
      data,
      token,
    }),

  // Replaces the per-car loop: one request, one transaction, only the cars that changed.
  // The old loop issued one call per car, took 10-21 minutes on a large catalogue, and
  // outlived the 15-minute access token — dying half-applied with a 401.
  upsertCarVariantsBulkV2: (
    offeringId: string,
    data: import('../types/offeringsV2').BulkUpsertCarVariantsRequest,
    token: string,
  ) =>
    request<import('../types/offeringsV2').BulkUpsertResult>({
      method: 'POST',
      url: `/api/garages/me/services-v2/${offeringId}/cars/variants-bulk`,
      data,
      token,
    }),

  cloneCarVariantsV2: (
    offeringId: string,
    targetCarId: string,
    srcCarId: string,
    token: string,
  ) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'POST',
      url: `/api/garages/me/services-v2/${offeringId}/cars/${targetCarId}/clone-from/${srcCarId}`,
      token,
    }),

  submitOfferingDraftsV2: (
    offeringId: string,
    data: import('../types/offeringsV2').SubmitOfferingDraftsRequest,
    token: string,
  ) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'POST',
      url: `/api/garages/me/services-v2/${offeringId}/submit`,
      data,
      token,
    }),

  // Spec 044 v2 edit: update an existing offering's basics.
  updateOfferingV2: (
    offeringId: string,
    data: import('../types/offeringsV2').UpdateOfferingV2Request,
    token: string,
  ) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'PUT',
      url: `/api/garages/me/services-v2/${offeringId}`,
      data,
      token,
    }),

  // Spec 044 v2 edit: replace the variation catalog (diff by id; removed values cascade).
  replaceVariationsV2: (
    offeringId: string,
    data: import('../types/offeringsV2').ReplaceVariationsRequest,
    token: string,
  ) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'PUT',
      url: `/api/garages/me/services-v2/${offeringId}/variations`,
      data,
      token,
    }),

  // Spec 044 v2 edit: replace service-level (car-less) variant prices for a non car-specific offering.
  upsertServiceVariantsV2: (
    offeringId: string,
    data: import('../types/offeringsV2').UpsertServiceVariantsRequest,
    token: string,
  ) =>
    request<import('../types/offeringsV2').OfferingV2Response>({
      method: 'PUT',
      url: `/api/garages/me/services-v2/${offeringId}/service-variants`,
      data,
      token,
    }),

  // Lookup endpoints
  fetchLookupEntries: (categoryCode: string, parentId?: string, token?: string) => {
    const params = new URLSearchParams();
    if (parentId) params.set('parentId', parentId);
    const qs = params.toString();
    return request<import('../types').LookupEntryResponse[]>({ method: 'GET', url: `/api/lookups/categories/${categoryCode}/entries${qs ? `?${qs}` : ''}`, token });
  },

  // Service proposal endpoints (owner)
  submitServiceProposal: (data: { proposedName: string; description?: string; suggestedCategoryId: string; price: number; durationMinutes: number; warrantyDescription?: string; carTypeIds?: string[] }, token: string) =>
    request<import('../types').ServiceProposalResponse>({ method: 'POST', url: '/api/garages/me/service-proposals', data, token }),

  fetchMyProposals: (token: string) =>
    request<import('../types').ServiceProposalResponse[]>({ method: 'GET', url: '/api/garages/me/service-proposals', token }),

  // Car types endpoint
  setServiceCarTypes: (serviceOfferingId: string, carTypeIds: string[], token: string) =>
    request<import('../types').ServiceOfferingResponse>({ method: 'PUT', url: `/api/garages/me/services/${serviceOfferingId}/car-types`, data: carTypeIds, token }),

  // Profile / Change Request endpoints
  submitChangeRequest: (data: unknown, token: string) =>
    request<unknown>({ method: 'POST', url: '/api/garages/me/change-requests', data, token }),

  getPendingChangeRequest: (token: string) =>
    request<unknown>({ method: 'GET', url: '/api/garages/me/change-requests/pending', token }),

  getMyChangeRequests: (token: string, status?: string) =>
    request<unknown[]>({ method: 'GET', url: `/api/garages/me/change-requests${status ? `?status=${status}` : ''}`, token }),

  toggleActivation: (active: boolean, token: string) =>
    request<void>({ method: 'PATCH', url: '/api/garages/me/activation', data: { active }, token }),

  // ── Settings endpoints ──

  changePassword: (data: import('../types/settings').ChangePasswordRequest, token: string) =>
    request<{ message: string }>({ method: 'POST', url: '/api/auth/change-password', data, token }),

  // ── My Profile (current user) ──

  getMyUser: (token: string) =>
    request<import('../types').MyUserResponse>({ method: 'GET', url: '/api/users/me', token }),

  updateMyUser: (data: import('../types').UpdateMyUserRequest, token: string) =>
    request<import('../types').MyUserResponse>({ method: 'PATCH', url: '/api/users/me', data, token }),

  requestUserPhoneChange: (newPhone: string, token: string) =>
    request<void>({ method: 'POST', url: '/api/users/me/phone/change-request', data: { newPhone }, token }),

  verifyUserPhoneChange: (newPhone: string, otp: string, token: string) =>
    request<import('../types').MyUserResponse>({ method: 'POST', url: '/api/users/me/phone/change-verify', data: { newPhone, otp }, token }),

  getNotificationPreferences: (token: string) =>
    request<import('../types/settings').NotificationPreferences>({ method: 'GET', url: '/api/notifications/preferences', token }),

  updateNotificationPreferences: (data: import('../types/settings').NotificationPreferences, token: string) =>
    request<import('../types/settings').NotificationPreferences>({ method: 'PUT', url: '/api/notifications/preferences', data, token }),

  updateGarageContact: (data: import('../types/settings').UpdateGarageContactRequest, token: string) =>
    request<{ email: string; phone: string }>({ method: 'PUT', url: '/api/garages/me/contact', data, token }),

  updateTradeLicense: (data: import('../types/settings').UpdateTradeLicenseRequest, token: string) =>
    request<import('../types/settings').TradeLicenseResponse>({ method: 'PUT', url: '/api/garages/me/trade-license', data, token }),

  getTradeLicenseDownloadUrl: (token: string) =>
    request<import('../types/settings').TradeLicenseDownloadResponse>({ method: 'GET', url: '/api/garages/me/trade-license/download-url', token }),

  listMyDocuments: (token: string) =>
    request<import('../types/settings').GarageDocumentResponse[]>({ method: 'GET', url: '/api/garages/me/documents', token }),

  submitDocument: (data: import('../types/settings').SubmitDocumentRequest, token: string) =>
    request<import('../types/settings').GarageDocumentResponse>({ method: 'POST', url: '/api/garages/me/documents', data, token }),

  deleteMyDocument: (documentId: string, token: string) =>
    request<void>({ method: 'DELETE', url: `/api/garages/me/documents/${documentId}`, token }),

  getGarageMembers: (token: string) =>
    request<import('../types/settings').GarageMemberResponse[]>({ method: 'GET', url: '/api/garages/me/members', token }),

  addGarageMember: (data: import('../types/settings').AddMemberRequest, token: string) =>
    request<import('../types/settings').GarageMemberResponse>({ method: 'POST', url: '/api/garages/me/members', data, token }),

  updateMemberRole: (memberId: string, data: import('../types/settings').UpdateMemberRequest, token: string) =>
    request<import('../types/settings').GarageMemberResponse>({ method: 'PUT', url: `/api/garages/me/members/${memberId}`, data, token }),

  // Spec 076 — OWNER/MANAGER only (403 for STAFF); the three values are stored as sent.
  updateMemberNotifications: (memberId: string, data: import('../types/settings').MemberNotificationSettings, token: string) =>
    request<import('../types/settings').GarageMemberResponse>({ method: 'PUT', url: `/api/garages/me/members/${memberId}/notifications`, data, token }),

  toggleMemberActivation: (memberId: string, active: boolean, token: string) =>
    request<import('../types/settings').GarageMemberResponse>({ method: 'PATCH', url: `/api/garages/me/members/${memberId}/activation`, data: { active }, token }),

  removeGarageMember: (memberId: string, token: string) =>
    request<void>({ method: 'DELETE', url: `/api/garages/me/members/${memberId}`, token }),

  getGarageUploadUrl: (data: { mediaType: string; fileName: string; contentType: string }, token: string) =>
    request<import('../types/settings').UploadUrlResponse>({ method: 'POST', url: '/api/garages/me/upload-url', data, token }),

  // Partner orders (F1 #162 — minimal stub for early review)
  listGarageOrders: (garageId: string, page: number, size: number, token: string) =>
    request<{
      content: Array<{
        id: string;
        bookingId: string;
        customerId: string;
        garageId: string;
        serviceOfferingId: string;
        vehicleId: string;
        status: string;
        totalAmount: number;
        currency: string;
        paymentId: string | null;
        createdAt: string;
        updatedAt: string;
      }>;
      totalElements: number;
      totalPages: number;
      number: number;
      size: number;
    }>({ method: 'GET', url: `/api/orders/garages/${garageId}?page=${page}&size=${size}`, token }),

  // Spec 046: Admin action feed
  getAdminActionFeed: (token: string, cursor: number | null, limit = 20) =>
    request<import('../types/offeringsV2').CursorPageResponse<import('../types/offeringsV2').AdminActionView>>({
      method: 'GET',
      url: `/api/garages/me/admin-actions?limit=${limit}${cursor != null ? `&cursor=${cursor}` : ''}`,
      token,
    }),

  getAdminActionUnreadCount: (token: string) =>
    request<{ count: number; capped: boolean }>({
      method: 'GET',
      url: '/api/garages/me/admin-actions/unread-count',
      token,
    }),

  markAdminActionsRead: (token: string) =>
    request<void>({
      method: 'POST',
      url: '/api/garages/me/admin-actions/mark-read',
      data: {},
      token,
    }),

  // ── Spec 062: Garage offers (partner) — /api/garages/me/offers ──

  listMyOffers: (token: string, status?: import('../types/offers').OfferListFilter, page = 0, size = 20) =>
    request<import('../types/offers').OfferPage>({
      method: 'GET',
      url: `/api/garages/me/offers?page=${page}&size=${size}${status ? `&status=${status}` : ''}`,
      token,
    }),

  /** Decision "4 preview": the server prices a draft rule per bookable variant. Read-only. */
  previewOffer: (data: import('../types/offers').OfferPreviewRequest, token: string) =>
    request<import('../types/offers').OfferPreviewResponse>({ method: 'POST', url: '/api/garages/me/offers/preview', data, token }),

  getMyOffer: (offerId: string, token: string) =>
    request<import('../types/offers').OfferResponse>({ method: 'GET', url: `/api/garages/me/offers/${offerId}`, token }),

  createOffer: (data: import('../types/offers').OfferUpsertRequest, token: string) =>
    request<import('../types/offers').OfferResponse>({ method: 'POST', url: '/api/garages/me/offers', data, token }),

  updateOffer: (offerId: string, data: import('../types/offers').OfferUpsertRequest, token: string) =>
    request<import('../types/offers').OfferResponse>({ method: 'PUT', url: `/api/garages/me/offers/${offerId}`, data, token }),

  // Lifecycle actions. The body carries the optimistic-lock version (BR-19) when known;
  // the server may ignore it for DRAFT.
  submitOffer: (offerId: string, token: string, version?: number) =>
    request<import('../types/offers').OfferResponse>({ method: 'POST', url: `/api/garages/me/offers/${offerId}/submit`, data: { version }, token }),

  withdrawOffer: (offerId: string, token: string, version?: number) =>
    request<import('../types/offers').OfferResponse>({ method: 'POST', url: `/api/garages/me/offers/${offerId}/withdraw`, data: { version }, token }),

  pauseOffer: (offerId: string, token: string, version?: number) =>
    request<import('../types/offers').OfferResponse>({ method: 'POST', url: `/api/garages/me/offers/${offerId}/pause`, data: { version }, token }),

  resumeOffer: (offerId: string, token: string, version?: number) =>
    request<import('../types/offers').OfferResponse>({ method: 'POST', url: `/api/garages/me/offers/${offerId}/resume`, data: { version }, token }),

  endOffer: (offerId: string, token: string, version?: number) =>
    request<import('../types/offers').OfferResponse>({ method: 'POST', url: `/api/garages/me/offers/${offerId}/end`, data: { version }, token }),

  archiveOffer: (offerId: string, token: string, version?: number) =>
    request<void>({ method: 'POST', url: `/api/garages/me/offers/${offerId}/archive`, data: { version }, token }),

  /**
   * Banner upload: upload-url (mediaType PROMOTION_BANNER, contentType, fileSize) → S3 PUT →
   * confirm-upload. Returns the storage key to send as `bannerStorageKey` / `bannerArStorageKey`.
   */
  uploadOfferBanner: async (
    file: File,
    token: string,
    onProgress?: (p: UploadProgress) => void,
  ): Promise<{ storageKey: string; downloadUrl: string | null }> => {
    const res = await uploadFile(file, 'PROMOTION_BANNER', token, onProgress);
    return { storageKey: res.storageKey, downloadUrl: res.downloadUrl ?? null };
  },

  // ── Spec 081b: Partner packages — /api/garages/me/packages ──
  // The cover image reuses uploadOfferBanner (PROMOTION_BANNER under the garage prefix).

  listMyPackages: (token: string, status?: import('../types/packages').PackageListFilter, page = 0, size = 50) =>
    request<import('../types/packages').PackagePage>({
      method: 'GET',
      url: `/api/garages/me/packages?page=${page}&size=${size}${status ? `&status=${status}` : ''}`,
      token,
    }),

  getMyPackage: (packageId: string, token: string) =>
    request<import('../types/packages').PackageResponse>({ method: 'GET', url: `/api/garages/me/packages/${packageId}`, token }),

  createPackage: (data: import('../types/packages').PackageRequest, token: string) =>
    request<import('../types/packages').PackageResponse>({ method: 'POST', url: '/api/garages/me/packages', data, token }),

  updatePackage: (packageId: string, data: import('../types/packages').PackageRequest, token: string) =>
    request<import('../types/packages').PackageResponse>({ method: 'PUT', url: `/api/garages/me/packages/${packageId}`, data, token }),

  /** Read-only: derived mode, totals, saving and every validation problem at once (errors[]). */
  previewPackage: (data: import('../types/packages').PackagePreviewRequest, token: string, signal?: AbortSignal) =>
    request<import('../types/packages').PackagePreviewResponse>({ method: 'POST', url: '/api/garages/me/packages/preview', data, token, signal, retry: true }),

  // Lifecycle actions carry the optimistic-lock version.
  submitPackage: (packageId: string, token: string, version?: number) =>
    request<import('../types/packages').PackageResponse>({ method: 'POST', url: `/api/garages/me/packages/${packageId}/submit`, data: { version }, token }),

  withdrawPackage: (packageId: string, token: string, version?: number) =>
    request<import('../types/packages').PackageResponse>({ method: 'POST', url: `/api/garages/me/packages/${packageId}/withdraw`, data: { version }, token }),

  pausePackage: (packageId: string, token: string, version?: number) =>
    request<import('../types/packages').PackageResponse>({ method: 'POST', url: `/api/garages/me/packages/${packageId}/pause`, data: { version }, token }),

  resumePackage: (packageId: string, token: string, version?: number) =>
    request<import('../types/packages').PackageResponse>({ method: 'POST', url: `/api/garages/me/packages/${packageId}/resume`, data: { version }, token }),

  endPackage: (packageId: string, token: string, version?: number) =>
    request<import('../types/packages').PackageResponse>({ method: 'POST', url: `/api/garages/me/packages/${packageId}/end`, data: { version }, token }),

  archivePackage: (packageId: string, token: string, version?: number) =>
    request<void>({ method: 'POST', url: `/api/garages/me/packages/${packageId}/archive`, data: { version }, token }),

  /** The order customers see running packages in (garage profile "Packages & offers" row). */
  reorderPackages: (orderedIds: string[], token: string) =>
    request<import('../types/packages').PackageResponse[]>({
      method: 'PUT',
      url: '/api/garages/me/packages/reorder',
      data: { orderedIds } satisfies import('../types/packages').ReorderRequest,
      token,
    }),
};

// ── Orders F1 typed API extensions ──────────────────────────────────────────

export type PartnerOrderFilter = 'ALL' | 'NEW' | 'IN_PROGRESS' | 'COMPLETED' | 'CLOSED';

/**
 * Spec 053 Flow D2 step 2 / AC-21b — partner-facing settlement status line.
 * `label` is server-rendered ("Completed, in hold until 6 Sep" → "Ready for settlement"
 * → "In settlement STL-…" → "Paid on <date>"); the page renders it verbatim.
 * Absent (or null) for orders that never reach settlement (Spec 049 NOT_APPLICABLE).
 */
export interface SettlementStatusLine {
  state: string;
  label: string;
  eligibleAt?: string | null;
  settlementNumber?: string | null;
  /** Settlement id — when present the number links to /earnings/<id>. */
  settlementId?: string | null;
  paidAt?: string | null;
}

/**
 * Spec 053 Phase 3 — a single delivery leg is either driven by Garagat or handled
 * by the customer. `deliveryMethod` stays on every response as a derived mirror
 * (delta D3/D7), so both shapes coexist; use `resolveLegMethods` in utils/orderLegs.
 */
export type LegMethod = 'GARAGAT' | 'SELF';

/**
 * Spec 072 v2 — how Garagat moves the car on its legs: a recovery truck, or a Zofeur driver who
 * drives it. Null when no leg is Garagat's; absent on an orders build before v2.
 */
export type TransportType = 'RECOVERY' | 'ZOFEUR';

/**
 * #918 — one picked option of a multi-select booking (list price, before any offer). #1193 — every
 * order now carries its lines: a single-variant pick is one line labelled with the picked values
 * ("Large · Premium"), and a flat service is one line labelled with the service name itself.
 */
export interface ServiceLine {
  variantId: string | null;
  variationValueId: string | null;
  label: string | null;
  price: number;
  durationMinutes: number | null;
}

export interface OrderListItem {
  id: string;
  orderNumber?: string;
  status: string;
  customerName?: string | null;
  vehicleMakeModel?: string | null;
  vehiclePlate?: string | null;
  vehicleSummary?: { plate?: string; make?: string; model?: string };
  serviceName?: string;
  /** #1193 — the booking's lines; use `selectedOptions` (utils/serviceLines) for what to show. */
  serviceLines?: ServiceLine[] | null;
  serviceCategory?: string | null;
  serviceSubcategory?: string | null;
  scheduledDate?: string;
  pickupSlotLabel?: string;
  /** #414 — human label of the pickup time slot chosen at booking (e.g. "9:00 AM – 11:00 AM"). */
  pickupTimeSlotLabel?: string | null;
  totalAmount: number;
  currency: string;
  deliveryMethod: 'DELIVERY' | 'SELF_SERVICE';
  /** Spec 053 Phase 3 — per-leg methods; absent on orders created before S10. */
  pickupMethod?: LegMethod | null;
  returnMethod?: LegMethod | null;
  /** VAT-inclusive Garagat pickup & delivery fee snapshotted at booking (BR-21). */
  deliveryFeeGross?: number | null;
  /** Number of Garagat-driven legs the fee was charged for (0–2). */
  deliveryTrips?: number | null;
  createdAt: string;
  /** Spec 053 AC-21b — settlement status line; present on list and detail responses. */
  settlementStatusLine?: SettlementStatusLine | null;
}

export type OrderCounts = Record<PartnerOrderFilter, number>;

export interface PickupDelivery {
  id: string;
  orderId: string;
  type: string;
  status: string;
  scheduledAt?: string | null;
  driverName?: string | null;
  driverPhone?: string | null;
  pickupAddress?: string | null;
  deliveryAddress?: string | null;
  notes?: string | null;
  completedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * One order-history entry (`PartnerOrderEventEntry`, Spec 069). `actorLabel` is the name to show
 * ("Garagat admin · Sara", "Ali", "Customer", "System"); `actorId` is present only for the garage's
 * own members, so the portal can add " (you)" — never render it.
 */
export interface OrderEvent {
  id: string;
  /** Not sent by the Spec 069 partner payload; kept optional for older responses. */
  orderId?: string;
  eventType: string;
  eventData?: Record<string, unknown> | null;
  actorLabel?: string | null;
  actorRole?: string | null;
  createdAt: string;
  actorId?: string | null;
}

export interface OrderBreakdown {
  serviceNet: number;
  vatAmount: number;
  vatRate: number;
  vatApplicable: boolean;
  platformFee: number;
  platformFeeVat: number;
  /** Spec 068 — the pre-promo customer total: service + Garagat pickup & delivery + Garagat service fee. */
  totalPayable: number;
  commissionRate: number | null;
  commissionAmount: number | null;
  vatOnCommission: number | null;
  partnerNet: number | null;
  derived: boolean;
  /**
   * Spec 059 — true when the customer used a Garagat-funded promo code. The partner view carries
   * no promo amounts: every figure here stays at the full price, because Garagat funds the
   * discount and the garage's earnings are unchanged. Absent on older orders builds.
   */
  promoApplied?: boolean;
  /**
   * Spec 068 §3.8 — the garage's service price (VAT-inclusive), the basis of its earnings. Absent on
   * an orders build before Spec 068; the page then falls back to `order.totalAmount`.
   */
  serviceGross?: number | null;
  /**
   * Spec 068 V17 — Garagat's service fee the customer paid, shown as INFORMATION in "Customer paid"
   * only; it is never the garage's money. 0.00/null/absent = no fee row.
   */
  serviceFeeGross?: number | null;
  serviceFeeNet?: number | null;
  serviceFeeVat?: number | null;
}

export interface OrderDetail extends OrderListItem {
  customerPhone?: string;
  /** #414 — the customer's booking note, copied from the booking to the order. */
  notes?: string | null;
  /** #414 — presigned URL for the customer's note image (never a raw storage key). */
  noteImageUrl?: string | null;
  pickupAddress?: { fullAddress?: string; latitude?: number; longitude?: number };
  assignedStaffUserIds: string[];
  disputeReason?: string;
  disputeType?: string;
  resolutionMessage?: string;
  pickupDeliveries?: PickupDelivery[];
  /** Spec 072 v2 (AC-15) — shown on each leg of the Assigned Driver card; use `transportLabel`. */
  transportType?: TransportType | null;
  /** #1178 — label of the customer's return slot; the Assigned Driver card's return-leg fallback. */
  returnTimeSlotLabel?: string | null;
  /** Spec 077 — the return day (yyyy-MM-dd); null/absent = the booking day (`scheduledDate`). */
  returnDate?: string | null;
  events?: OrderEvent[];
  // Spec 049 — per-order price/earnings breakdown (partner view) + payout state
  breakdown?: OrderBreakdown | null;
  settlementState?: string | null;
  /** #1435 — the saved vehicle's body type at booking (CAR_TYPES code, e.g. SUV); null when unknown. */
  vehicleType?: string | null;
  /** #1435 — 'UNDER' when the booked size option is smaller than the vehicle; null otherwise. */
  sizeMismatch?: 'UNDER' | null;
  /** #1435 — the booked option label(s) that are too small, e.g. "Hatchback / Sedan". */
  sizeBookedAs?: string | null;
  /** #1435 — true when the customer saw the app's size warning and chose Proceed. */
  sizeWarningAcknowledged?: boolean | null;
}

export interface PagedOrders {
  content: OrderListItem[];
  totalPages: number;
  number: number;
  totalElements: number;
}

export const listGarageOrdersFiltered = (
  garageId: string,
  status: PartnerOrderFilter,
  assignedToMe: boolean,
  page: number,
  size: number,
  token: string,
  search?: string,
) => {
  const params = new URLSearchParams({
    status,
    assignedToMe: String(assignedToMe),
    page: String(page),
    size: String(size),
  });
  const q = search?.trim();
  if (q) params.set('search', q);
  return api.get<PagedOrders>(`/api/orders/garages/${garageId}?${params.toString()}`, token);
};

export const getGarageOrderCounts = (
  garageId: string,
  assignedToMe: boolean,
  token: string,
  search?: string,
) => {
  const params = new URLSearchParams({ assignedToMe: String(assignedToMe) });
  const q = search?.trim();
  if (q) params.set('search', q);
  return api.get<OrderCounts>(`/api/orders/garages/${garageId}/counts?${params.toString()}`, token);
};

export const getOrderDetail = (orderId: string, token: string) =>
  api.get<OrderDetail>(`/api/orders/${orderId}`, token);

/**
 * All three garage status actions answer with the LIST-shaped `OrderResponse`
 * (`orderMapper.toResponse`), NOT the detail payload — no `breakdown`, no `events`, no
 * `pickupDeliveries`, no `pickupAddress`, no `assignedStaffUserIds`. They are typed
 * `OrderListItem` for that reason, so nothing can feed a response straight back into the
 * detail page's state; callers re-read `getOrderDetail` instead.
 */
export const markServiceComplete = (orderId: string, token: string) =>
  api.patch<OrderListItem>(`/api/orders/${orderId}/service-complete`, {}, token);

/**
 * Spec 053 BR-22 / AC-36 — the two garage-facing leg actions.
 *
 * Both are idempotent on the backend (repeat calls return 200 with no second status
 * event), which is why they opt into the network-error retry that POSTs skip by
 * default. A leg that cannot reach the target state answers 409 with an explanatory
 * message ("Transition not allowed for this order's delivery choice", BR-23) —
 * callers surface `ApiError.message` verbatim.
 */
export const markVehicleReceived = (orderId: string, token: string) =>
  request<OrderListItem>({
    method: 'POST',
    url: `/api/orders/${orderId}/vehicle-received`,
    data: {},
    token,
    retry: true,
  });

export const markCollectedByCustomer = (orderId: string, token: string) =>
  request<OrderListItem>({
    method: 'POST',
    url: `/api/orders/${orderId}/collected`,
    data: {},
    token,
    retry: true,
  });

// ── Spec 051 — partner invoice documents (AC-9) ─────────────────────────────

export interface InvoiceDocument {
  id: string;
  docType: 'INVOICE' | 'CREDIT_NOTE';
  invoiceNumber: string | null; // null while HELD_SUPPLIER_DATA
  status: 'HELD_SUPPLIER_DATA' | 'PENDING_DOCUMENT' | 'ISSUED';
  issueDate: string | null;
  grossAmount: number | null;
  vatAmount: number | null;
  currency: string;
  originalInvoiceNumber: string | null; // credit note → its invoice
}

export interface InvoiceDocumentsResponse {
  documents: InvoiceDocument[];
}

export const getOrderInvoices = (orderId: string, token: string) =>
  api.get<InvoiceDocumentsResponse>(`/api/orders/${orderId}/invoices`, token);

// 409 INVOICE_NOT_READY while the document is not ISSUED yet ("being prepared").
export const getOrderInvoiceDownloadUrl = (orderId: string, documentId: string, token: string) =>
  api.get<{ downloadUrl: string; expiresInSeconds: number }>(
    `/api/orders/${orderId}/invoices/${documentId}/download-url`,
    token,
  );
